import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import noop from 'lodash/noop';
import {
  fieldIssueTypesType,
  locationsTreeNodesType,
  oxygenType,
  queryParamsType
} from '../../constants/proptype-validation';
import {statusColorMapping} from '../../constants/status-types';
import {TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {ISSUE_ATTRIBUTES_DATA_TYPES} from '../../constants/issue-attributes';
import i18n from '../../utils/i18n';
import {preloadedFilterQueryParamsState} from '../../constants/preloaded-states';
import {toIsoDate} from '../../utils/date-helpers';
import onClickOutside from 'react-onclickoutside';
import {ISSUE_FILTER_PROPERTIES, PAGE_PARAMS} from '../../constants/issue-query-params';
import {getAssignees} from '../../utils/assigned-to-helpers';
import {sortAlphabeticallyByPath} from '../../utils/sortingHelpers';
import {getUserCompanyName} from '../../utils/userHelpers';
import Button from '@adsk/bim360-matrix-react-button';
import Checkbox from '@adsk/bim360-matrix-react-checkbox';
import DatePicker from '@adsk/bim360-matrix-react-date-picker';
import DateSelector from '@adsk/bim360-matrix-react-date-selector';
import LocationPicker from '@adsk/bim360-matrix-react-location-picker';
import Label from '@adsk/bim360-matrix-react-label';
import {StatusColorIndicatorOption} from '@adsk/bim360-matrix-react-status-color-indicator';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';
import {WithScrollableContainerRef} from '@adsk/bim360-matrix-react-helpers';
import SearchBoxWithValidatorWrapper from './SearchBoxWithValidatorWrapper';
import SelectBoxAdapter from '../common/SelectBoxAdapter';
import {SUB_LOCATION_LIMIT} from '../../constants/location-filter';
import {subLocationTooltipStyle} from '../../utils/location-helper';
import {linkedToOptions} from '../../constants/linkedTo';
import {getIssueCustomAttributeInputValidationMessage} from '../../utils/validation-helpers';

const scrollableClassname = 'StickyScrollableContainer__content';
const CUSTOM_ATTRIBUTE_DROPDOWN_KEY = 'CUSTOM_ATTRIBUTE_DROPDOWN_KEY';
const CUSTOM_ATTRIBUTE_DROPDOWN_FORCE_RENDER_KEY = 'CUSTOM_ATTRIBUTE_DROPDOWN_FORCE_RENDER_KEY';

class Filter extends PureComponent {
  constructor(props) {
    super(props);

    this.state = this.initialQueryState(props.queryParams, props.customAttributes);

    this.DatePickerComponent = props.useFilterPanel
      ? WithScrollableContainerRef(DatePicker, scrollableClassname)
      : DatePicker;
    this.SelectDropdownComponent = SelectBoxAdapter;
    this.LocationPickerComponent = props.useFilterPanel
      ? WithScrollableContainerRef(LocationPicker, scrollableClassname)
      : LocationPicker;

    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleSelectCreatedBy = this.handleSelectCreatedBy.bind(this);
    this.handleSelectOwner = this.handleSelectOwner.bind(this);
    this.handleSelectAssignee = this.handleSelectAssignee.bind(this);
    this.handleIssueSubTypeChange = this.handleIssueSubTypeChange.bind(this);
    this.handleSelectCustomAttribute = this.handleSelectCustomAttribute.bind(this);
    this.setQueryParams = this.setQueryParams.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (
      this.props.queryParams !== nextProps.queryParams ||
      this.props.customAttributes !== nextProps.customAttributes
    ) {
      this.setState(this.initialQueryState(nextProps.queryParams, nextProps.customAttributes));
    }
  }

  componentWillUpdate(nextProps, nextState) {
    if (
      !isEqual(
        this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES],
        nextState[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES]
      )
    ) {
      this.setState(
        {
          [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]: this.state[
            ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES
          ].filter(({id}) =>
            nextState[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES]
              .map(selectedCustomAttribute => selectedCustomAttribute.id)
              .includes(id)
          )
        },
        this.handleSubmitIfFilterPanel
      );
    }
  }

  getCustomAttributesStateFromQueryParams(queryParams, customAttributes) {
    let selectedCustomAttributesQueryState, parsedSelectedCustomAttributesValues;

    try {
      parsedSelectedCustomAttributesValues =
        (this.state && this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]) ||
        JSON.parse(queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]);
    } catch (error) {
      parsedSelectedCustomAttributesValues = [];
    }

    if (
      this.state &&
      this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES] &&
      this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES].length
    ) {
      selectedCustomAttributesQueryState = this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES];
    } else {
      const customAttributesIdsFromQueryParams = parsedSelectedCustomAttributesValues.map(({id}) => id);
      selectedCustomAttributesQueryState = [...customAttributes].filter(customAttribute =>
        customAttributesIdsFromQueryParams.includes(customAttribute.id)
      );
    }

    // Set custom attributes values to [] when a user choose "Clear All"
    if (
      !(
        queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES] &&
        queryParams[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES].length
      )
    ) {
      parsedSelectedCustomAttributesValues = [];
    }

    return {
      [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES]: selectedCustomAttributesQueryState,
      [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]: parsedSelectedCustomAttributesValues
    };
  }

  initialQueryState(queryParams, customAttributes) {
    return {
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.STATUS_FILTER].slice(0),
      [ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER].slice(0),
      [ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER].slice(
        0
      ),
      [ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: getAssignees(
        queryParams[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER],
        this.props.oxygen
      ),
      [ISSUE_FILTER_PROPERTIES.OWNER_FILTER]: getAssignees(
        queryParams[ISSUE_FILTER_PROPERTIES.OWNER_FILTER],
        this.props.oxygen
      ),
      [ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: getAssignees(
        queryParams[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER],
        this.props.oxygen
      ),
      [ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER].slice(
        0
      ),
      [ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER],
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER],
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER],
      [ISSUE_FILTER_PROPERTIES.SEARCH]: queryParams[ISSUE_FILTER_PROPERTIES.SEARCH],
      [ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS]: queryParams[ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS],
      [ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER]: queryParams[ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER],
      ...this.getCustomAttributesStateFromQueryParams(queryParams, customAttributes),
      ...this.getDisabledSubLocationCheck(queryParams)
    };
  }
  getDisabledSubLocationCheck(queryParams) {
    return {disabledSublocation: this.isLimitExceeded(queryParams[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER])};
  }

  getPreloadedQueryParamsState() {
    return preloadedFilterQueryParamsState[this.props.viewType][TYPE_QUALITY_ISSUES];
  }

  getPreloadedQueryParam(key) {
    const preloadedFilterState = this.getPreloadedQueryParamsState();
    return preloadedFilterState[key];
  }

  handleClickOutside() {
    this.props.displayFilterDropdown(false);
  }

  handleSelectCreatedBy(valueArray) {
    this.setState({[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: valueArray}, this.handleSubmitIfFilterPanel);
  }

  handleSelectAssignee(valueArray) {
    this.setState({[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: valueArray}, this.handleSubmitIfFilterPanel);
  }

  handleSelectOwner(valueArray) {
    this.setState({[ISSUE_FILTER_PROPERTIES.OWNER_FILTER]: valueArray}, this.handleSubmitIfFilterPanel);
  }

  handleSelectCustomAttribute(valueArray) {
    this.setState({[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES]: valueArray}, this.setQueryParams);
  }

  removeCustomAttributeValuesFromState(customAttributeIdToRemove) {
    const selectedCustomAttributesValues = this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES];

    this.setState(
      {
        [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]: selectedCustomAttributesValues.filter(
          ({id}) => id !== customAttributeIdToRemove
        )
      },
      this.handleSubmitIfFilterPanel
    );
  }

  handleSelectValueForCustomAttribute(value, id) {
    const currentCustomAttributes = this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]
      ? [...this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]]
      : [];

    const existingCustomAttribute = currentCustomAttributes.find(customAttribute => customAttribute.id === id);
    if (existingCustomAttribute) {
      currentCustomAttributes.splice(currentCustomAttributes.indexOf(existingCustomAttribute), 1);
    }
    value && currentCustomAttributes.push({id, value});

    this.setState(
      {[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]: currentCustomAttributes},
      this.handleSubmitIfFilterPanel
    );
  }

  setQueryParams() {
    const {setQueryParamsAction} = this.props;

    setQueryParamsAction({
      [PAGE_PARAMS.OFFSET]: 0,
      [ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.STATUS_FILTER],
      [ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER],
      [ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER],
      [ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.ISSUE_TYPE_FILTER],
      [ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER],
      [ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER].map(
        user => user.oxygenId
      ),
      [ISSUE_FILTER_PROPERTIES.OWNER_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.OWNER_FILTER].map(
        user => user.oxygenId
      ),
      [ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER].map(
        x => x.oxygenId
      ),
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER],
      [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER],
      [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER],
      [ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS]: this.state[ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS],
      [ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES]: this.state[
        ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES
      ].length
        ? JSON.stringify(this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES])
        : '',
      [ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER]: this.state[ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER]
    });
  }

  handleSubmitIfFilterPanel() {
    this.props.useFilterPanel && this.handleSubmit();
  }

  handleSubmit() {
    const {displayFilterDropdown} = this.props;

    this.setQueryParams();
    displayFilterDropdown(false);
  }

  handleReset() {
    const {setQueryParamsAction, displayFilterDropdown} = this.props;

    this.setState(this.getPreloadedQueryParamsState());
    setQueryParamsAction(this.getPreloadedQueryParamsState());

    displayFilterDropdown(false);
  }

  handleDatePickerDueDateChange(date) {
    if (date === null) {
      this.setState(
        {
          [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]: '',
          [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]: ''
        },
        this.handleSubmitIfFilterPanel
      );
    } else {
      this.setState(
        {
          [ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]: toIsoDate(date.startDate),
          [ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]: toIsoDate(date.endDate)
        },
        this.handleSubmitIfFilterPanel
      );
    }
  }

  handleDateSelectorChange(date, startDateProperty, endDateProperty) {
    const state = {};
    if (date.startDate !== undefined) {
      state[startDateProperty] = toIsoDate(date.startDate) || '';
    }
    state[endDateProperty] = toIsoDate(date.endDate) || '';

    this.setState(state, this.handleSubmitIfFilterPanel);
  }

  handleDateSelectorDueDateChange(date) {
    this.handleDateSelectorChange(
      date,
      ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER,
      ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER
    );
  }

  handleDateSelectorCreatedAtChange(date) {
    this.handleDateSelectorChange(
      date,
      ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER,
      ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER
    );
  }

  handleDatePickerCreatedAtChange(date) {
    if (date === null) {
      this.setState(
        {
          [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]: '',
          [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]: ''
        },
        this.handleSubmitIfFilterPanel
      );
    } else {
      this.setState(
        {
          [ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]: toIsoDate(date.startDate),
          [ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]: toIsoDate(date.endDate)
        },
        this.handleSubmitIfFilterPanel
      );
    }
  }

  handleStatusChange(statues) {
    this.setState(
      {[ISSUE_FILTER_PROPERTIES.STATUS_FILTER]: statues.map(status => status.value)},
      this.handleSubmitIfFilterPanel
    );
  }

  handleIssueSubTypeChange(types) {
    this.setState(
      {[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER]: types.map(t => t.id)},
      this.handleSubmitIfFilterPanel
    );
  }

  isLimitExceeded(locationId) {
    return (
      this.props.subLocationCounts.get(locationId) && this.props.subLocationCounts.get(locationId) >= SUB_LOCATION_LIMIT
    );
  }

  handleLocationFilterChange(location) {
    const isLimitExceeded = this.isLimitExceeded(location.id);
    const subLocationEnabled = this.state[ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS];
    this.setState(
      {
        [ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]: location
          ? location.id
          : this.getPreloadedQueryParam(ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER),
        [ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS]:
          isLimitExceeded && subLocationEnabled ? null : subLocationEnabled,
        disabledSublocation: isLimitExceeded
      },
      this.handleSubmitIfFilterPanel
    );
  }

  handleIncludeSublocationsFilterChange(value) {
    this.setState(
      {
        [ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS]: value || null
      },
      this.handleSubmitIfFilterPanel
    );
  }

  handleRootCauseFilterChange(rootCauses) {
    this.setState(
      {
        [ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER]: rootCauses.map(rootCause => rootCause.id)
      },
      this.handleSubmitIfFilterPanel
    );
  }

  handleLinkedToChange(options) {
    this.setState(
      {
        [ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER]: options.map(option => option.value)
      },
      this.handleSubmitIfFilterPanel
    );
  }

  renderIssueSubTypeFilter() {
    const {SelectDropdownComponent} = this;
    if (this.props.filterBySubTypeEnabled) {
      const {subTypesGroupedDropdownOptions} = this.props;
      const allOptions = subTypesGroupedDropdownOptions.reduce((arr, group) => arr.concat(group[1]), []);
      const selectedIssueSubTypeFilters = allOptions.filter(subType =>
        this.state[ISSUE_FILTER_PROPERTIES.ISSUE_SUB_TYPE_FILTER].includes(subType.id)
      );
      return (
        <div className="Filter__field">
          <Label className="Filter__field-label">{i18n.t('filter.issueTypeAndSubType')}</Label>
          <SelectDropdownComponent
            options={subTypesGroupedDropdownOptions}
            selectedOptions={selectedIssueSubTypeFilters}
            grouped
            multi
            clearable
            searchable
            labelKey="title"
            valueKey="id"
            placeholder={i18n.t('filter.issueTypePlaceholder')}
            onChange={this.handleIssueSubTypeChange}
            searchIcon
            searchPlaceholder={i18n.t('filter.issueTypeSearchPlaceholder')}
          />
        </div>
      );
    }
    return null;
  }

  renderStatus() {
    const {statusOptions} = this.props;
    const {SelectDropdownComponent} = this;
    const selectedStatusFilters = statusOptions.filter(status =>
      this.state[ISSUE_FILTER_PROPERTIES.STATUS_FILTER].includes(status.value)
    );

    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('new.status')}</Label>
        <SelectDropdownComponent
          selectedOptions={selectedStatusFilters}
          options={statusOptions}
          optionRenderer={(value, label) => (
            <StatusColorIndicatorOption statusColor={statusColorMapping[value]} label={label} />
          )}
          onChange={event => this.handleStatusChange(event)}
          placeholder={i18n.t('filter.statusPlaceholder')}
          clearable
          multi
        />
      </div>
    );
  }

  renderLocationPicker() {
    const {isLocationsTreeLoading, isLocationsLoadingError, locationsTreeNodes, getDefaultLocationTree} = this.props;
    const {LocationPickerComponent} = this;
    const checkboxClass = cx('Filter__field-location-checkbox', {
      'Filter__field-location-checkbox-disabled': this.state.disabledSublocation
    });
    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('filter.location')}</Label>
        <LocationPickerComponent
          placeholder={i18n.t('filter.locationPlaceholder')}
          searchPlaceholder={i18n.t('filter.locationSearchPlaceholder')}
          onChange={event => this.handleLocationFilterChange(event)}
          locationApiPayload={locationsTreeNodes}
          isLoading={isLocationsTreeLoading}
          loadingError={isLocationsLoadingError}
          onTryAgain={getDefaultLocationTree}
          selectedEventKey={this.state[ISSUE_FILTER_PROPERTIES.LBS_LOCATION_FILTER]}
        />
        <Tooltip
          theme={Tooltip.Themes.DARK}
          place={Tooltip.Places.TOP}
          content={this.state.disabledSublocation
            ? i18n.t('filter.subLocationLimit', {subLocationsLimit: SUB_LOCATION_LIMIT})
            : null}
          style={subLocationTooltipStyle}
        >
          <Checkbox
            label={i18n.t('filter.locationIncludeSublocations')}
            className={checkboxClass}
            checked={this.state[ISSUE_FILTER_PROPERTIES.INCLUDE_SUBLOCATIONS]}
            onChange={event => this.handleIncludeSublocationsFilterChange(event)}
            disabled={this.state.disabledSublocation}
          />
        </Tooltip>
      </div>
    );
  }

  renderRootCauseDropdown() {
    const {issueRootCausesGroupedDropdownOptions} = this.props;
    const {SelectDropdownComponent} = this;

    const allOptions = issueRootCausesGroupedDropdownOptions.reduce((arr, group) => arr.concat(group[1]), []);
    const selectedRootCausesFilters = allOptions.filter(rootCause =>
      this.state[ISSUE_FILTER_PROPERTIES.ROOT_CAUSE_ID_FILTER].includes(rootCause.id)
    );
    const textNoAvailableRootCause = issueRootCausesGroupedDropdownOptions.length
      ? undefined
      : i18n.t('new.noAvailableRootCauses');
    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('filter.rootCause')}</Label>
        <SelectDropdownComponent
          options={issueRootCausesGroupedDropdownOptions}
          selectedOptions={selectedRootCausesFilters}
          grouped
          multi
          clearable
          searchable
          labelKey="title"
          valueKey="id"
          placeholder={i18n.t('filter.rootCausePlaceholder')}
          onChange={event => this.handleRootCauseFilterChange(event)}
          searchIcon
          searchPlaceholder={i18n.t('filter.rootCauseSearchPlaceholder')}
          noResultsValue={textNoAvailableRootCause}
        />
      </div>
    );
  }

  renderCreatedByDropdown() {
    const {SelectDropdownComponent} = this;
    const {projectUsers} = this.props;

    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('filter.createdBy')}</Label>
        <SelectDropdownComponent
          options={projectUsers}
          selectedOptions={this.state[ISSUE_FILTER_PROPERTIES.CREATED_BY_FILTER]}
          multi
          labelKey="name"
          valueKey="oxygenId"
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.selectUserPlaceholder')}
          className="IssueCreateForm__field-width"
          onChange={this.handleSelectCreatedBy}
          clearable
          searchable
          searchIcon
        />
      </div>
    );
  }

  renderCreatedAtDropdown() {
    const {DatePickerComponent} = this;
    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('filter.createdAt')}</Label>
        {this.props.isDateSelectorMigrationEnabled && (
          <DateSelector
            inputProps={{placeholder: i18n.t('filter.chooseDatePlaceholder')}}
            range
            startDate={this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]
              ? new Date(this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER])
              : null}
            endDate={this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
              ? new Date(this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER])
              : null}
            onChange={date => this.handleDateSelectorCreatedAtChange(date)}
            popupContainer={this}
          />
        )}
        {!this.props.isDateSelectorMigrationEnabled && (
          <DatePickerComponent
            placeholder={i18n.t('filter.chooseDatePlaceholder')}
            range
            startDate={this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]
              ? this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_START_FILTER]
              : null}
            endDate={this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
              ? this.state[ISSUE_FILTER_PROPERTIES.CREATED_AT_END_FILTER]
              : null}
            onChange={date => this.handleDatePickerCreatedAtChange(date)}
          />
        )}
      </div>
    );
  }

  renderAssignedTo() {
    const {SelectDropdownComponent} = this;
    const {groupedProjectEntities} = this.props;

    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('assignment.to')}</Label>
        <SelectDropdownComponent
          options={groupedProjectEntities}
          selectedOptions={this.state[ISSUE_FILTER_PROPERTIES.ASSIGNED_TO_FILTER]}
          grouped
          multi
          clearable
          searchable
          labelKey="name"
          valueKey="oxygenId"
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.assignedToPlaceholder')}
          onChange={this.handleSelectAssignee}
          searchIcon
          searchPlaceholder={i18n.t('filter.assignedToSearch')}
          clearInputOnSelect={false}
        />
      </div>
    );
  }

  renderOwner() {
    const {SelectDropdownComponent} = this;
    const {projectUsers} = this.props;

    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('filter.owner')}</Label>
        <SelectDropdownComponent
          options={projectUsers}
          selectedOptions={this.state[ISSUE_FILTER_PROPERTIES.OWNER_FILTER]}
          multi
          clearable
          searchable
          labelKey="name"
          valueKey="oxygenId"
          subtextGetter={getUserCompanyName}
          placeholder={i18n.t('filter.ownerPlaceholder')}
          onChange={this.handleSelectOwner}
          searchIcon
          searchPlaceholder={i18n.t('filter.ownerSearch')}
        />
      </div>
    );
  }

  renderLinkedTo() {
    const {SelectDropdownComponent} = this;
    const selectedOptions = linkedToOptions.filter(option =>
      this.state[ISSUE_FILTER_PROPERTIES.LINKED_TO_FILTER].includes(option.value)
    );

    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('new.linkedTo')}</Label>
        <SelectDropdownComponent
          selectedOptions={selectedOptions}
          options={linkedToOptions}
          onChange={event => this.handleLinkedToChange(event)}
          placeholder={i18n.t('new.searchDocumentChecklist')}
          clearable
          multi
        />
      </div>
    );
  }

  renderDueDate() {
    const {isDateSelectorMigrationEnabled} = this.props;
    const {DatePickerComponent} = this;

    return (
      <div className="Filter__field">
        <Label className="Filter__field-label">{i18n.t('new.dueDate')}</Label>
        {isDateSelectorMigrationEnabled && (
          <DateSelector
            inputProps={{placeholder: i18n.t('filter.chooseDatePlaceholder')}}
            range
            startDate={this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]
              ? new Date(this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER])
              : null}
            endDate={this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
              ? new Date(this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER])
              : null}
            onChange={date => this.handleDateSelectorDueDateChange(date)}
            popupContainer={this}
          />
        )}
        {!isDateSelectorMigrationEnabled && (
          <DatePickerComponent
            placeholder={i18n.t('filter.chooseDatePlaceholder')}
            range
            startDate={this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]
              ? this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_START_FILTER]
              : null}
            endDate={this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
              ? this.state[ISSUE_FILTER_PROPERTIES.DUE_DATE_END_FILTER]
              : null}
            onChange={date => this.handleDatePickerDueDateChange(date)}
          />
        )}
      </div>
    );
  }

  renderCustomAttribute(customAttribute) {
    let componentToRender;
    const {id, title, dataType} = customAttribute;

    const selectedCustomAttribute = (this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES_VALUES] || []).find(
      selectedCustomAttributeValues => selectedCustomAttributeValues.id === id
    );
    const selected = selectedCustomAttribute ? selectedCustomAttribute.value : undefined;

    switch (dataType) {
      case ISSUE_ATTRIBUTES_DATA_TYPES.SINGLE_LIST:
        const {SelectDropdownComponent} = this;

        componentToRender = (
          <SelectDropdownComponent
            key={selectedCustomAttribute ? CUSTOM_ATTRIBUTE_DROPDOWN_KEY : CUSTOM_ATTRIBUTE_DROPDOWN_FORCE_RENDER_KEY}
            options={customAttribute.metadata.list.options}
            selectedOptions={selected}
            clearable
            searchable
            searchIcon
            searchPlaceholder={i18n.t('filter.customAttributesSearch')}
            labelKey="value"
            valueKey="id"
            onChange={value => this.handleSelectValueForCustomAttribute(value, id)}
            className="Filter__custom-attribute-dropdown"
          />
        );
        break;
      case ISSUE_ATTRIBUTES_DATA_TYPES.TEXT:
      case ISSUE_ATTRIBUTES_DATA_TYPES.PARAGRAPH:
      case ISSUE_ATTRIBUTES_DATA_TYPES.NUMERIC:
        componentToRender = (
          <SearchBoxWithValidatorWrapper
            searchExpression={selected}
            validate={(searchExpression) => getIssueCustomAttributeInputValidationMessage(dataType, searchExpression)}
            onClear={() => this.removeCustomAttributeValuesFromState(customAttribute.id)}
            onSearch={searchExpression =>
              this.handleSelectValueForCustomAttribute(searchExpression, customAttribute.id)}
          />
        );
        break;
      default:
        throw new Error(`Unknown dataType: ${dataType}`);
    }

    return (
      <div className="Filter__field" key={id}>
        <Label className="Filter__field-label">{title}</Label>
        {componentToRender}
      </div>
    );
  }

  renderCustomAttributes() {
    if (this.props.filterByCustomAttributesEnabled) {
      const {SelectDropdownComponent} = this;
      const {customAttributes} = this.props;

      const sortedSelectedCustomAttributes = sortAlphabeticallyByPath(
        this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES],
        'title'
      );

      return (
        <React.Fragment>
          <Label className="Filter__fields-title">{i18n.t('filter.customFilters')}</Label>
          <div className="Filter__field">
            <Label className="Filter__field-label">{i18n.t('filter.selectCustomAttributes')}</Label>
            <SelectDropdownComponent
              options={customAttributes}
              selectedOptions={this.state[ISSUE_FILTER_PROPERTIES.SELECTED_CUSTOM_ATTRIBUTES]}
              multi
              clearable
              searchable
              searchIcon
              searchPlaceholder={i18n.t('filter.customAttributesSearch')}
              labelKey="title"
              valueKey="id"
              placeholder={i18n.t('filter.customAttributesPlaceholder')}
              onChange={this.handleSelectCustomAttribute}
              className="Filter__custom-attributes-dropdown"
              dropdownMaxWidth="100%"
            />
          </div>
          {sortedSelectedCustomAttributes.map(customAttribute => this.renderCustomAttribute(customAttribute))}
        </React.Fragment>
      );
    }
    return null;
  }

  getFilter() {
    const {useFilterPanel} = this.props;

    const className = cx('Filter', {
      'Filter--matrix': useFilterPanel
    });

    return (
      <div className={className}>
        <Label className="Filter__fields-title">{i18n.t('filter.defaultFilters')}</Label>

        {/*If you add more fields in to filter remember to change the height in the css class: '$issues-panel-header-height'*/}
        {this.renderIssueSubTypeFilter()}

        {/* Status */}
        {this.renderStatus()}

        {/* Location */}
        {this.renderLocationPicker()}

        {/* Owner */}
        {this.renderOwner()}

        {/*Assigned To New*/}
        {this.renderAssignedTo()}

        {/* Due Date range */}
        {this.renderDueDate()}

        {/* Root Cause */}
        {this.renderRootCauseDropdown()}

        {/* Created by for unified issues (different placement) */}
        {this.renderCreatedByDropdown()}

        {/*Created at range for unified issues (different placement)*/}
        {this.renderCreatedAtDropdown()}

        {/* Custom Attributes */}
        {this.renderCustomAttributes()}

        {/* Linked to */}
        {this.props.showLinkedToFilter && this.renderLinkedTo()}

        {/* Footer */}
        {!useFilterPanel && (
          <div className="Filter__actions">
            <Button
              styleType={Button.StyleType.LINK_SECONDARY}
              className="Filter__reset-button"
              onClick={this.handleReset.bind(this)}
            >
              {i18n.t('navigation.reset')}
            </Button>
            <Button
              styleType={Button.StyleType.PRIMARY}
              className="Filter__apply-button"
              onClick={() => this.handleSubmit.bind(this)()}
            >
              {i18n.t('navigation.apply')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  render() {
    return this.props.visible ? this.getFilter() : null;
  }
}

Filter.displayName = 'Filter';

Filter.defaultProps = {
  displayFilterDropdown: noop
};

Filter.propTypes = {
  visible: PropTypes.bool.isRequired,
  groupedProjectEntities: PropTypes.array.isRequired,
  projectUsers: PropTypes.array.isRequired,
  statusOptions: PropTypes.array.isRequired,
  oxygen: oxygenType.isRequired,
  setQueryParamsAction: PropTypes.func.isRequired,
  queryParams: queryParamsType.isRequired,
  displayFilterDropdown: PropTypes.func,
  fieldIssueTypes: fieldIssueTypesType.isRequired,
  isLocationsTreeLoading: PropTypes.bool,
  isLocationsLoadingError: PropTypes.bool,
  locationsTreeNodes: locationsTreeNodesType,
  getDefaultLocationTree: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  filterBySubTypeEnabled: PropTypes.bool,
  subTypesGroupedDropdownOptions: PropTypes.array,
  issueRootCausesGroupedDropdownOptions: PropTypes.array,
  useFilterPanel: PropTypes.bool,
  isDateSelectorMigrationEnabled: PropTypes.bool,
  container: PropTypes.object,
  filterByCustomAttributesEnabled: PropTypes.bool,
  customAttributes: PropTypes.array,
  subLocationCounts: PropTypes.instanceOf(Map),
  showLinkedToFilter: PropTypes.bool.isRequired
};

export default onClickOutside(Filter);
